import { useId } from 'react';
import type { Control, FieldValues, Path } from 'react-hook-form';
import { useController } from 'react-hook-form';
import styled from 'styled-components';

import { lineClamp } from '@xing-com/crate-utils-line-clamp';
import { IconWarning } from '@xing-com/icons';
import { Label } from '@xing-com/label';
import { RadioButton } from '@xing-com/radio-button';
import { RadioButtonGroup } from '@xing-com/radio-button-group';
import {
  scale060,
  scale080,
  scale110,
  spaceM,
  spaceXS,
  xdlColorError,
} from '@xing-com/tokens';
import { Meta } from '@xing-com/typography';

import { useHelperText } from './hooks';

export type RadioFieldProps<T extends FieldValues> = {
  control: Control<T>;
  path: Path<T>;
  options: { value: string; label: string }[];
  required?: boolean;
  errorMessage?: string;
  requiredErrorMessage?: string;
  'aria-labelledby'?: string;
};

export const RadioField = <T extends FieldValues>({
  control,
  path,
  options,
  required = false,
  errorMessage,
  requiredErrorMessage,
  'aria-labelledby': ariaLabelledBy,
}: RadioFieldProps<T>): React.ReactElement => {
  const radioId = useId();
  const {
    field: { onChange, value: selectedValue, ref, onBlur },
    fieldState: { error },
  } = useController({
    control,
    name: path,
    rules: { required: required },
  });
  const getHelperText = useHelperText({
    requiredErrorMessage,
    errorMessage,
  });

  //Allows deleting selection when field is not required by clicking twice on the same radio
  const handleOptionClick = (optionValue: string): void => {
    if (!required && selectedValue === optionValue) {
      onChange('');
    }
  };

  return (
    <>
      <RadioButtonGroup aria-labelledby={ariaLabelledBy}>
        {options.map(({ value, label }, index) => (
          <StyledLabel key={value}>
            <RadioButton
              name={`radio-${radioId}`}
              onChange={onChange}
              onClick={() => handleOptionClick(value)}
              onBlur={onBlur}
              value={value}
              on={selectedValue === value}
              {...(index === 0 ? { ref } : {})}
              aria-labelledby={`${radioId}-option-${value}`}
            />
            <LabelContent id={`${radioId}-option-${value}`} aria-hidden>
              {label}
            </LabelContent>
          </StyledLabel>
        ))}
      </RadioButtonGroup>

      {error ? (
        <ErrorMessage lineHeight="dense" role="alert" size="xsmall">
          <ErrorIcon aria-hidden="true" />
          {getHelperText(error)}
        </ErrorMessage>
      ) : null}
    </>
  );
};

const StyledLabel = styled(Label)`
  display: flex;
  flex-direction: row;
  gap: ${spaceM};
  font-size: ${scale080};
  line-height: ${scale110};
`;

const LabelContent = styled.span`
  ${lineClamp(2)}
`;

const ErrorMessage = styled(Meta)`
  display: flex;
  margin: ${spaceXS} 0;
  gap: ${spaceXS};
  && {
    color: ${xdlColorError};
  }
`;

export const ErrorIcon = styled(IconWarning)`
  flex-shrink: 0;
  height: ${scale060};
  width: ${scale060};
`;
